const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function sb(path, params = {}) {
  const qs  = new URLSearchParams(params).toString();
  const url = `${SUPABASE_URL}/rest/v1/${path}${qs ? '?' + qs : ''}`;
  const res = await fetch(url, {
    headers: {
      'apikey':        SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type':  'application/json',
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Supabase ${res.status}: ${body}`);
  }
  return res.json();
}

export async function fetchBoardGames(date) {
  const cols = [
    'game_id','date','status','scheduled_time','game_number',
    'away_abbr','away_name','home_abbr','home_name',
    'away_pitcher','away_era',
    'home_pitcher','home_era',
    'away_starter_confirmed','home_starter_confirmed',
    'away_rs_30','away_rs_7','away_ra_30','away_ra_7',
    'home_rs_30','home_rs_7','home_ra_30','home_ra_7',
    'venue_name','park_factor','roof_type',
    'temp_f','wind_mph','wind_direction','rain_probability','conditions',
    'ump_name','ump_k_diff',
    'away_bullpen','home_bullpen',
    'chaos_total','chaos_tier',
    'edge_pct','projected_total','market_line',
'p_over',
    'confidence_score','confidence_tier',
    'oracle_eligible','oracle_score',
    'snap_line_movement','snap_line_open','snap_line_current','snap_book_spread',
    'conf_weather','conf_line_move','conf_sharp_money','conf_umpire',
    'conf_park','conf_bullpen','conf_pitcher','conf_model_proj',
    'away_runs','home_runs','inning','inning_half','outs',
    'on_first','on_second','on_third','balls','strikes',
    'last_play','current_batter','current_pitcher',
    'away_hits','home_hits','away_errors','home_errors',
  ].join(',');
  const url = `${SUPABASE_URL}/rest/v1/board_games?date=eq.${date}&order=scheduled_time.asc&select=${cols}`;
  const res = await fetch(url, {
    headers: {
      'apikey':        SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
  });
  if (!res.ok) throw new Error(`fetchBoardGames ${res.status}`);
  return res.json();
}

export async function fetchAllOdds(gameIds) {
  if (!gameIds.length) return {};
  const rows = await sb('odds_latest', { game_id: `in.(${gameIds.join(',')})`, select: '*' });
  const result = {};
  for (const row of rows) {
    if (!result[row.game_id]) result[row.game_id] = {};
    result[row.game_id][row.book] = {
      awayML: row.away_ml, homeML: row.home_ml,
      overOdds: row.over_odds, underOdds: row.under_odds,
    };
  }
  return result;
}

export async function fetchOracle(date) {
  const rows = await sb('oracle_with_context', { date: `eq.${date}`, select: '*', limit: '1' });
  return rows[0] ?? null;
}

export async function fetchTrackRecord(limit = 30) {
  return sb('oracle_with_context', {
    result: 'not.eq.PENDING', select: 'date,pick,book,odds,edge_pct,confidence_tier,result,pnl_units,actual_total,away_abbr,home_abbr',
    order: 'date.desc', limit: String(limit),
  });
}

export async function fetchPlayResults(limit = 50) {
  return sb('play_results', {
    select: 'id,date,game_id,direction,line,confidence_tier,edge_pct,result,actual_total,pnl_units,recorded_at,away_abbr,home_abbr',
    order: 'recorded_at.desc',
    limit: String(limit),
  });
}

export async function fetchLiveGames() {
  const cols = [
    'game_id','scheduled_time','date',
    'away_abbr','home_abbr','away_name','home_name',
    'total_line',
    'chaos_total','edge_pct',
    'inning','inning_half','outs',
    'away_runs','home_runs','away_hits','home_hits',
    'away_errors','home_errors',
    'on_first','on_second','on_third',
    'last_play','balls','strikes',
    'current_batter','current_batter_id',
    'current_pitcher','current_pitcher_id',
    'current_pitcher_pitches','current_pitcher_avg_limit',
    'away_active_pitcher_id','home_active_pitcher_id',
    'away_pitch_count','home_pitch_count',
    'live_updated_at',
  ].join(',');
  return sb('live_games', { select: cols, order: 'scheduled_time.asc' });
}

export async function fetchGamePitchers(gameId) {
  return sb('game_pitchers', { game_id: `eq.${gameId}`, select: '*', order: 'side.asc,is_starter.desc,entry_inning.asc' });
}

export async function fetchBatters() {
  return sb('batters', {
    select: '*,teams(abbr)',
    order: 'adjusted_due_factor.desc.nullslast,due_factor.desc',
  });
}

export async function fetchTodaysPitchers(date) {
  const d = date || new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const rows = await sb('board_games', { date: `eq.${d}`, select: 'away_abbr,home_abbr,away_pitcher,home_pitcher', order: 'scheduled_time.asc' });
  const pitchers = [];
  for (const g of rows) {
    if (g.away_pitcher) pitchers.push({ name: g.away_pitcher, team: g.away_abbr, side: 'away', avg: 88, current: 0 });
    if (g.home_pitcher) pitchers.push({ name: g.home_pitcher, team: g.home_abbr, side: 'home', avg: 88, current: 0 });
  }
  return pitchers;
}

export async function fetchAiPredictions(date) {
  const d = date || new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  return sb('ai_predictions', { date: `eq.${d}`, select: '*', order: 'created_at.desc' });
}

export async function saveAiPrediction(prediction) {
  await fetch(`${SUPABASE_URL}/rest/v1/ai_predictions`, {
    method: 'POST',
    headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', 'Prefer': 'return=minimal' },
    body: JSON.stringify(prediction),
  });
}

export async function fetchPollVotes(gameId, date) {
  const d = date || new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const rows = await sb('game_polls', { game_id: `eq.${gameId}`, date: `eq.${d}`, select: '*', limit: '1' });
  return rows[0] ?? { over_votes: 0, under_votes: 0 };
}

export async function upsertPollVote(gameId, dir, date) {
  const d = date || new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const existing = await fetchPollVotes(gameId, d);
  const newOver  = existing.over_votes  + (dir === 'over'  ? 1 : 0);
  const newUnder = existing.under_votes + (dir === 'under' ? 1 : 0);
  await fetch(`${SUPABASE_URL}/rest/v1/game_polls`, {
    method: 'POST',
    headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', 'Prefer': 'resolution=merge-duplicates,return=minimal' },
    body: JSON.stringify({ game_id: gameId, date: d, over_votes: newOver, under_votes: newUnder }),
  });
}

export async function fetchBookStates() {
  const rows = await sb('book_states', { select: 'book,state,active', active: 'eq.true' });
  const result = {};
  for (const row of rows) {
    if (!result[row.book]) result[row.book] = new Set();
    if (row.active) result[row.book].add(row.state);
  }
  return result;
}

export async function fetchGameCounts(dates) {
  if (!dates.length) return {};
  const rows = await sb('games', { date: `in.(${dates.join(',')})`, select: 'date,game_id' });
  const counts = {};
  for (const row of rows) {
    counts[row.date] = (counts[row.date] || 0) + 1;
  }
  return counts;
}

export async function fetchTeamTrends() {
  const rows = await sb('results', { select: 'game_id,total_runs', order: 'created_at.desc', limit: '100' });
  return rows;
}

export async function fetchLineMoves(gameId) {
  return sb('line_movement_by_book', {
    game_id: `eq.${gameId}`,
    select:  'book,opening_line,current_line,movement',
    order:   'book.asc',
  });
}